'use client';

import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import { commonClient, type CountryItem } from '@bip/api-client';
import { SearchBox } from './SearchBox';

interface CountrySelectProps {
  id?: string;
  name?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (code: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** Show the dial code next to country names. Useful when picker is reused for phone-country selection. */
  showDialCode?: boolean;
  hasError?: boolean;
}

/**
 * Modern, searchable country combobox. Replaces the previous native `<select>`.
 *
 * - Value is the ISO 3166-1 alpha-2 country code (e.g. "GB").
 * - Country list is bundled with `react-phone-number-input` so codes always
 *   line up with the phone-input's flag picker.
 * - Renders a hidden `<input>` carrying the same value/name so existing
 *   form submission logic and the `required` attribute keep working.
 */
export function CountrySelect({
  id,
  name,
  value,
  defaultValue,
  onChange,
  placeholder = 'Select country',
  required,
  disabled,
  className,
  showDialCode = false,
  hasError = false,
}: CountrySelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [internalValue, setInternalValue] = useState(value ?? defaultValue ?? '');
  const [canonical, setCanonical] = useState<CountryItem[] | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;

  // Fetch the canonical country list from common-service on mount. Falls back
  // to the bundled `react-phone-number-input` list (below) when the fetch
  // fails or returns empty, so the picker still works offline / pre-deploy.
  useEffect(() => {
    let cancelled = false;
    commonClient
      .getCountries()
      .then((res) => {
        if (cancelled) return;
        const list = res?.data;
        if (Array.isArray(list) && list.length > 0) setCanonical(list);
      })
      .catch(() => {
        // Keep bundled fallback — nothing to do.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Bundled fallback list, derived from `react-phone-number-input`. */
  const fallbackOptions = useMemo(() => {
    const codes = getCountries();
    return codes
      .map((code) => ({
        code,
        label: (en as Record<string, string>)[code] || code,
        dial: showDialCode ? `+${getCountryCallingCode(code)}` : '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [showDialCode]);

  const options = useMemo(() => {
    if (!canonical || canonical.length === 0) return fallbackOptions;
    return canonical
      .map((c) => ({
        code: c.code,
        label: c.name || c.code,
        dial: showDialCode ? c.dialCode || '' : '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [canonical, fallbackOptions, showDialCode]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.code.toLowerCase().includes(q) || o.dial.includes(q),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const active = filtered[activeIdx];
    if (active) optionRefs.current[active.code]?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIdx, filtered]);

  const selected = options.find((o) => o.code === currentValue);

  function select(code: string) {
    if (!isControlled) setInternalValue(code);
    onChange?.(code);
    setOpen(false);
    setQuery('');
    setActiveIdx(0);
    triggerRef.current?.focus();
  }

  function onKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = filtered[activeIdx];
      if (c) select(c.code);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  // The 1px border is applied via inline style, NOT Tailwind's `border-*` utilities.
  // This project's build gives `@layer base` higher cascade priority than
  // `@layer utilities` (see app/styles/base.css), so the base reset
  // `button { border: none }` beats any `border`/`border-solid`/`border-<color>`
  // utility on this <button> trigger — leaving the Country field with no visible
  // outline while the sibling <input> fields kept theirs. Inline styles win over
  // every layered rule, so they restore a consistent 1px border. The focus ring is
  // box-shadow-based (unaffected by the border reset) so it stays a utility.
  const borderStyle: CSSProperties = {
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: hasError ? '#fca5a5' /* red-300 */ : '#ced4da',
  };
  const ringCls = hasError ? 'focus:ring-red-200' : 'focus:ring-primary/20';

  return (
    <div className={`relative ${className || ''}`}>
      {/* Hidden input keeps the form value queryable + supports `required` semantics */}
      <input type="hidden" id={id} name={name} value={currentValue || ''} required={required} />

      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={borderStyle}
        className={`w-full bg-white rounded-md px-3 py-2.5 text-sm text-left outline-none focus:ring-1 transition-colors flex items-center justify-between gap-2 ${ringCls} disabled:opacity-60 disabled:cursor-not-allowed`}
      >
        <span className={selected ? 'text-secondary' : 'text-info'}>
          {selected ? (showDialCode ? `${selected.label} (${selected.dial})` : selected.label) : placeholder}
        </span>
        <i className={`fa-solid fa-chevron-down text-info text-xs transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          ref={popoverRef}
          onKeyDown={onKey}
          tabIndex={-1}
          className="absolute z-30 mt-1 w-full bg-white border border-border-light rounded-xl shadow-lg overflow-hidden"
          role="listbox"
        >
          <div className="p-2 border-b border-border-light">
            <SearchBox
              value={query}
              onChange={(v) => {
                setQuery(v);
                setActiveIdx(0);
              }}
              placeholder="Search country…"
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-info text-center">No matches</div>
            ) : (
              filtered.map((o, i) => {
                const isActive = i === activeIdx;
                const isSelected = o.code === currentValue;
                return (
                  <div
                    key={o.code}
                    ref={(el) => {
                      optionRefs.current[o.code] = el;
                    }}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => select(o.code)}
                    className={`flex items-center justify-between px-3 py-2 text-base cursor-pointer ${
                      isActive ? 'bg-purple-light' : ''
                    } ${isSelected ? 'font-semibold text-primary' : 'text-secondary'}`}
                  >
                    <span>{o.label}</span>
                    {showDialCode && <span className="text-info text-xs">{o.dial}</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
